import { Ban, Check, Compass, GitBranch, Headphones, Maximize, Mic, MicOff, Monitor, Music, PhoneOff, PictureInPicture2, ScrollText, ShieldCheck, Smile, Users, Volume2, X } from 'lucide-react'
import { cx } from '@/lib/cx'
import styles from './LandingDemos.module.css'

/*
 * Кусочки интерфейса для главной. Скриншот один — окно приложения; остальное вёрстка: лёгкая,
 * чёткая на любом экране. Люди и сервер те же, что на скриншоте и в рекламе: «Клан «Вечерние»»,
 * Лёха, Марго, Тимур, Дэн. Для экранных дикторов каждая иллюстрация — одна картинка с подписью.
 */

const IMG = {
  app: '/landing/app-1120.webp',
  app2x: '/landing/app-2240.webp',
  shooter: '/landing/stream-shooter.webp',
  dota: '/landing/stream-dota.webp',
  den: '/landing/avatar-den.webp',
}

function LiveBadge({ small = false }: { small?: boolean }) {
  return (
    <span className={cx(styles.live, small && styles.liveSmall)}>
      <span className={styles.liveDot} />В эфире
    </span>
  )
}

function StreamWindow({ className, src, name, viewers }: { className?: string | undefined; src: string; name: string; viewers?: string }) {
  return (
    <div className={cx(styles.stream, className)}>
      <img className={styles.frame} src={src} alt="" width={1200} height={675} decoding="async" />
      <LiveBadge />
      <span className={styles.quality}>4K · 60 к/с</span>
      <span className={styles.streamFoot}>
        <img className={styles.avatar} src={IMG.den} alt="" width={28} height={28} />
        <span className={styles.streamName}>{name}</span>
        {viewers && <span className={styles.viewers}>{viewers}</span>}
      </span>
    </div>
  )
}

function MusicCard({ className, big = false }: { className?: string | undefined; big?: boolean }) {
  return (
    <div className={cx(styles.music, big && styles.musicBig, className)}>
      <span className={styles.musicHead}>
        {big ? <span className={styles.cover} /> : <span className={styles.musicIcon}><Music size={22} /></span>}
        <span className={styles.musicWho}>
          {big ? (
            <>
              <span className={styles.musicName}>Ночной эфир — lo-fi</span>
              <span className={styles.musicWhere}>в «Лобби» · в очереди ещё 4</span>
            </>
          ) : (
            <>
              <span className={styles.musicName}>Кличат Музыка <span className={styles.bot}>бот</span></span>
              <span className={styles.musicWhere}>играет в «Лобби»</span>
            </>
          )}
        </span>
      </span>
      {!big && <span className={styles.track}>Ночной эфир — lo-fi</span>}
      <span className={styles.bar}><span /></span>
      <span className={styles.times}><span>1:24</span><span>3:40</span></span>
    </div>
  )
}

/** Первый экран: окно Кличата, поверх — стрим в 4K и музыкальный бот. */
export function HeroStage() {
  return (
    <div className={styles.stage} role="img" aria-label="Окно Кличата: сервер «Клан «Вечерние»», отряд собирается в #общий, в голосовом «Лобби» четверо; поверх — стрим Дэна в 4K и музыкальный бот">
      <span className={styles.stageGlow} />
      <span className={styles.window}>
        <span className={styles.windowBar}><i /><i /><i /></span>
        <img
          src={IMG.app}
          srcSet={`${IMG.app} 1120w, ${IMG.app2x} 2240w`}
          sizes="(max-width: 719px) 1px, (min-width: 1280px) 1120px, 90vw"
          width={1120}
          height={700}
          alt=""
          fetchPriority="high"
        />
      </span>
      <StreamWindow className={styles.stageStream} src={IMG.shooter} name="Дэн стримит" viewers="смотрят 12" />
      <MusicCard className={styles.stageMusic} />
    </div>
  )
}

export function StreamDemo() {
  return (
    <div className={styles.player} role="img" aria-label="Стрим Dota 2 в 4K при 60 кадрах; второй стрим открыт «картинкой в картинке»">
      <span className={styles.playerFrame}>
        <img className={styles.frame} src={IMG.dota} alt="" width={1080} height={608} loading="lazy" decoding="async" />
        <LiveBadge />
        <span className={styles.quality}>4K · 60 к/с</span>
        <span className={styles.streamFoot}>
          <span className={styles.playerTools}>
            <span><PictureInPicture2 size={18} /></span>
            <span><Maximize size={18} /></span>
          </span>
        </span>
      </span>
      <span className={styles.pip}>
        <img className={styles.frame} src={IMG.shooter} alt="" width={1200} height={675} loading="lazy" decoding="async" />
        <span className={styles.pipLabel}>Картинка в картинке</span>
      </span>
    </div>
  )
}

export function VoiceDemo() {
  return (
    <div className={styles.voice} role="img" aria-label="Голосовой канал «Лобби»: говорит Лёха, у Марго громкость 140 %, Тимур без микрофона, Дэн стримит; включено шумоподавление RNNoise">
      <span className={styles.voiceHead}>
        <Volume2 size={20} className={styles.muted} />
        <span className={styles.voiceName}>Лобби</span>
        <span className={styles.voiceCount}>4 в канале</span>
        <span className={styles.chip}><Check size={14} strokeWidth={3} className={styles.accentIcon} />Шумоподавление RNNoise</span>
        <span className={cx(styles.chip, styles.ping)}>38 мс</span>
      </span>
      <span className={styles.tiles}>
        <span className={cx(styles.tile, styles.speaking)}>
          <span className={cx(styles.face, styles.orange)}>Л</span>
          <span className={styles.tag}>Лёха</span>
        </span>
        <span className={styles.tile}>
          <span className={cx(styles.face, styles.pink)}>М</span>
          <span className={styles.tag}>Марго</span>
          <span className={styles.volume}>
            <span className={styles.volumeRow}><span>Громкость</span><b>140 %</b></span>
            <span className={styles.slider}><span /><i /></span>
          </span>
        </span>
        <span className={styles.tile}>
          <span className={cx(styles.face, styles.green)}>Т</span>
          <span className={styles.tag}><MicOff size={13} className={styles.mutedMic} />Тимур</span>
        </span>
        <span className={cx(styles.tile, styles.tileStream)}>
          <img className={styles.frame} src={IMG.dota} alt="" width={1080} height={608} loading="lazy" decoding="async" />
          <LiveBadge small />
          <span className={styles.tag}><img src={IMG.den} alt="" width={18} height={18} />Дэн</span>
        </span>
      </span>
      <span className={styles.controls}>
        <span><Mic size={22} /></span>
        <span><Headphones size={22} /></span>
        <span><Monitor size={22} /></span>
        <span className={styles.leave}><PhoneOff size={22} /></span>
      </span>
    </div>
  )
}

const ROLES = [
  { name: 'Админ', color: '#f0883e', text: '#f0883e', count: 2 },
  { name: 'Стример', color: '#b57cff', text: '#c9a4ff', count: 1, selected: true },
  { name: 'Командир', color: '#3ba55c', text: '#57c27a', count: 2 },
  { name: '@everyone', color: '#7c7f92', text: '#c5c8d6', count: 48 },
]
const PERMISSIONS = [
  { name: 'Просматривать каналы', on: true },
  { name: 'Отправлять сообщения', on: true },
  { name: 'Упоминать @everyone', on: false },
  { name: 'Управлять каналами', on: false },
  { name: 'Выгонять участников', on: false },
]
/** Страницы для поисковиков про игры (web/seo/pages/igry): по ним же находят каталог. */
const GAMES = [
  { name: 'CS2', href: '/igry/cs2' },
  { name: 'Dota 2', href: '/igry/dota-2' },
  { name: 'Deadlock', href: '/igry/deadlock' },
  { name: 'Мир танков', href: '/igry/mir-tankov' },
  { name: 'GTA RP', href: '/igry/gta-rp' },
]

/** Плитки раздела «Серверы»: роли, автомодерация, ветки, журнал, приглашения, каталог, эмодзи. */
export function ServerTiles({ onCatalog }: { onCatalog: () => void }) {
  return (
    <div className={styles.bento}>
      <article className={cx(styles.tileCard, styles.roles)}>
        <h3 className={styles.tileTitle}><ShieldCheck size={22} className={styles.accentIcon} />Роли и права</h3>
        <p className={styles.tileText}>Права — на весь сервер, категорию или один канал.</p>
        <div
          className={styles.rolesDemo}
          role="img"
          aria-label="Роли «Админ», «Стример», «Командир» и @everyone; у роли «Стример» включены просмотр каналов и сообщения, выключены упоминания @everyone, управление каналами и выгон участников"
        >
          <span className={styles.roleList}>
            {ROLES.map((r) => (
              <span key={r.name} className={cx(styles.role, r.selected && styles.roleSelected)}>
                <i style={{ background: r.color }} />
                <b style={{ color: r.text }}>{r.name}</b>
                <span>{r.count}</span>
              </span>
            ))}
          </span>
          <span className={styles.permsTitle}>Права роли «Стример»</span>
          <span className={styles.perms}>
            {PERMISSIONS.map((p) => (
              <span key={p.name} className={styles.perm}>
                {p.name}
                <i className={cx(styles.toggle, p.on && styles.toggleOn)} />
              </span>
            ))}
          </span>
        </div>
      </article>

      <article className={cx(styles.tileCard, styles.automod)}>
        <h3 className={styles.tileTitle}><Ban size={22} className={styles.accentIcon} />Автомодерация</h3>
        <p className={styles.tileText}>Фильтр слов, спама и массовых упоминаний.</p>
        <div className={styles.blocked} role="img" aria-label="Сообщение kitsune скрыто правилом «Реклама»">
          <span className={styles.blockedHead}><span className={cx(styles.face, styles.violet)}>K</span><b>kitsune</b><span>14:02</span></span>
          <span className={styles.blockedNote}><Ban size={14} />Скрыто правилом «Реклама»</span>
        </div>
      </article>

      <article className={cx(styles.tileCard, styles.threads)}>
        <h3 className={styles.tileTitle}><GitBranch size={22} className={styles.accentIcon} />Ветки</h3>
        <p className={styles.tileText}>Обсуждения не тонут в общем чате.</p>
        <ul className={styles.threadList} aria-label="Ветки канала">
          <li><span>Сборка отряда</span><span>12</span></li>
          <li><span>Клипы недели</span><span>5</span></li>
          <li><span>Вопросы новичков</span><span>8</span></li>
        </ul>
      </article>

      <article className={cx(styles.tileCard, styles.audit)}>
        <h3 className={styles.tileTitle}><ScrollText size={22} className={styles.accentIcon} />Журнал аудита</h3>
        <p className={styles.tileText}>Кто и что изменил на сервере.</p>
        <ul className={styles.auditList} aria-label="Записи журнала аудита">
          <li><i className={styles.red} /><span><b>Саня</b> выдал роль «Стример» Дэну</span><time>14:02</time></li>
          <li><i className={styles.teal} /><span><b>Влад</b> создал канал #клипы</span><time>13:47</time></li>
          <li><i className={styles.red} /><span><b>Саня</b> изменил права в #правила</span><time>13:10</time></li>
        </ul>
      </article>

      <article className={cx(styles.tileCard, styles.invite)}>
        <p className={styles.inviteLabel}>Приглашение на сервер</p>
        <div className={styles.inviteDemo} role="img" aria-label="Приглашение на сервер «Клан «Вечерние»»: 12 в сети, 48 участников">
          <span className={styles.inviteHead}>
            <span className={styles.guildIcon}>КВ</span>
            <span className={styles.inviteWho}>
              <b>Клан «Вечерние»</b>
              <span><i className={styles.onlineDot} />12 в сети</span>
              <span><i className={styles.offlineDot} />48 участников</span>
            </span>
          </span>
          <span className={styles.join}>Присоединиться</span>
        </div>
      </article>

      <article className={cx(styles.tileCard, styles.catalog)}>
        <h3 className={styles.tileTitle}><Compass size={22} className={styles.accentIcon} />Каталог серверов</h3>
        <p className={styles.tileText}>Откройте сервер для всех — его найдут в каталоге по категории и тегам.</p>
        <div className={styles.games}>
          {GAMES.map((g) => (
            <a key={g.href} className={styles.game} href={g.href}>{g.name}</a>
          ))}
          <button type="button" className={styles.catalogLink} onClick={onCatalog}>Смотреть каталог</button>
        </div>
      </article>

      <article className={cx(styles.tileCard, styles.emoji)}>
        <h3 className={styles.tileTitle}><Smile size={22} className={styles.accentIcon} />Эмодзи, стикеры и гифки</h3>
        <p className={styles.tileText}>Свои эмодзи и стикеры на сервере, гифки — поиском прямо в поле ввода.</p>
        <div className={styles.reactions} role="img" aria-label="Реакции под сообщением: огонь 3, палец вверх 5, глаза 2">
          <span className={styles.reactionMine}>🔥 3</span>
          <span>👍 5</span>
          <span>👀 2</span>
          <span className={styles.gif}>GIF</span>
        </div>
      </article>
    </div>
  )
}

const IMPORTED = ['Категории и каналы с правами', 'Роли', 'Ветки', 'Эмодзи и стикеры', 'Иконка и настройки']

export function ImportDemo() {
  return (
    <div className={styles.importCard} role="img" aria-label="Импорт из Discord завершён: перенесены категории и каналы с правами, роли, ветки, эмодзи и стикеры, иконка и настройки; сообщения не переносятся; участники получают роли по кодам переноса">
      <span className={styles.importHead}>
        <span className={styles.guildIcon}>КВ</span>
        <span className={styles.importWho}><b>Импорт из Discord</b><span>Клан «Вечерние» · готово</span></span>
        <span className={styles.done}><Check size={14} strokeWidth={3} />Перенесено</span>
      </span>
      <span className={styles.progress} />
      <span className={styles.importGrid}>
        {IMPORTED.map((item) => (
          <span key={item} className={styles.importItem}><Check size={18} strokeWidth={3} className={styles.accentIcon} />{item}</span>
        ))}
        <span className={cx(styles.importItem, styles.importSkip)}><X size={18} strokeWidth={2.4} />Сообщения — нет</span>
      </span>
      <span className={styles.importNote}><Users size={20} className={styles.accentIcon} />Коды переноса готовы: участники вводят код и получают свои роли.</span>
    </div>
  )
}

export function MusicDemo() {
  return (
    <div className={styles.musicDemo} role="img" aria-label="Кличат Музыка играет «Ночной эфир — lo-fi» в «Лобби», в очереди ещё 4 трека; команды !играй, !скип, !пауза, !очередь, !громкость">
      <MusicCard big />
      <span className={styles.commands}>
        <code>!играй</code>
        <code>!скип</code>
        <code>!пауза</code>
        <code>!очередь</code>
        <code>!громкость 80</code>
      </span>
    </div>
  )
}

export function BotCode() {
  return (
    <pre className={styles.code} aria-label="Бот на discord.js: в настройках клиента указан адрес API Кличата">
      <code>
        <span className={styles.codeLine}><span className={styles.kw}>const</span>{' client = '}<span className={styles.kw}>new</span>{' Client({'}</span>
        <span className={styles.codeLine}>{'  intents: [GatewayIntentBits.Guilds],'}</span>
        <span className={cx(styles.codeLine, styles.codeMark)}>{'  rest: { api: '}<span className={styles.str}>{"'https://klichat.ru/api'"}</span>{' },'}</span>
        <span className={styles.codeLine}>{'})'}</span>
        <span className={styles.codeLine}>{'client.login(process.env.KLICHAT_BOT_TOKEN)'}</span>
      </code>
    </pre>
  )
}
